'use client'

import * as React from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Command, Compass, LayoutDashboard } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore, useSearchStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { navPillCls, navPillLabelCls } from './nav-pill'
import { LanguageToggle } from './language-toggle'
import { LikedButton } from './liked-button'
import { SavedSearchesButton } from './saved-searches-button'
import { NotificationBell } from './notification-bell'
import { UserMenu } from '@/components/auth'
import { CompactSearchInput, FiltersButton } from '@/components/search'
import { useI18n } from '@/lib/i18n'

interface HeaderProps {
  variant?: 'default' | 'search'
}

export function Header({ variant: propVariant }: HeaderProps) {
  const pathname = usePathname()
  const router = useRouter()
  const { setCommandPaletteOpen } = useUIStore()
  const { resetSearch } = useSearchStore()
  const { t } = useI18n()
  const [isMac, setIsMac] = React.useState(false)
  const showHeaderSearch = pathname !== '/'

  // Auto-detect variant based on route if not explicitly provided
  const variant = propVariant ?? (pathname === '/search' ? 'search' : 'default')
  
  // Detect platform for keyboard shortcut display
  React.useEffect(() => {
    setIsMac(/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform))
  }, [])
  
  const handleLogoClick = () => {
    resetSearch()
  }
  
  const handleTextSearch = () => {
    if (pathname !== '/search') {
      router.push('/search')
      return
    }
    // Dispatch custom event for search page to listen to (text search source)
    window.dispatchEvent(new CustomEvent('header-search-triggered', { detail: { source: 'text' } }))
  }

  const handleFilterApply = () => {
    if (pathname !== '/search') {
      router.push('/search')
      return
    }
    // Dispatch custom event for search page to listen to (filter search source)
    window.dispatchEvent(new CustomEvent('header-search-triggered', { detail: { source: 'filters' } }))
  }
  
  return (
    <header className="sticky top-0 z-40 w-full bg-background/80 backdrop-blur-xl border-b border-border/40">
      {/* Full-bleed row (design shell.jsx header, padding 0 24px) — the
          search bar is guaranteed HALF the screen (user 2026-07-16: "should
          always take half of the screen... top priority"), so the row can't
          afford the container cap's dead margins. */}
      <div className="w-full px-4 sm:px-6 h-[90px] flex items-center">
        {/* Left section - Logo */}
        <div className="flex items-center flex-shrink-0">
          <Link href="/" onClick={handleLogoClick} className="flex items-end gap-2 pb-0.5">
            <img
              src="/logo.png"
              alt="Roostr"
              className="h-12 w-auto flex-shrink-0"
            />
            {/* Wordmark yields to the 50vw search bar (design hides it in
                compact mode too). */}
            <span className="hidden min-[1500px]:inline font-display text-xl font-bold text-foreground leading-none mb-1.5">
              Roostr
            </span>
            {/* Beta tag — present on phones and desktop alike. Gated to 360px
                and up because the 320px header has only ~22px of slack in its
                centre column (the search button is a fixed 48px), and this
                must not re-create the control collision fixed earlier. */}
            <span className="mb-1.5 hidden rounded-full border border-primary/25 bg-primary/10 px-1.5 py-px text-[9.5px] font-bold uppercase leading-[1.5] tracking-[0.6px] text-primary min-[360px]:inline-block">
              {t('header.beta', 'Beta')}
            </span>
          </Link>
        </div>
        
        {/* Center section - Search controls (truly centered). From lg (the
            desktop input form) the cluster is pinned to HALF the viewport —
            never squeezed by the nav; the right cluster's visibility ladder
            below is tuned to fit the other half. Below lg the tappable
            mobile pill keeps its flexible width. */}
        <div className="flex-1 flex justify-center px-2 sm:px-4 min-w-0">
          {showHeaderSearch && (
            <div className="flex items-center gap-2 w-full max-w-xl min-w-0 lg:w-auto lg:max-w-none lg:shrink-0">
              {/* The input itself (not the input+filters cluster) is the
                  half-screen guarantee. */}
              {/* flex-1 only from sm — below that the trigger is a fixed
                  40px icon circle and must not be stretched back into a
                  wide empty pill by this override. */}
              <CompactSearchInput onSearch={handleTextSearch} className="sm:flex-1 min-w-0 lg:w-[50vw] lg:flex-none" />
              {/* Hidden below sm. This pill is `flex-shrink-0` (60px) inside a
                  `flex-1 min-w-0` centre column that collapses to ~70px at
                  320px, so it used to overflow its parent and render ON TOP of
                  the right cluster (measured: 40px overlap with LikedButton on
                  an iPhone SE). Nothing is lost — /search already carries a
                  Filters chip with the same count in MobileQuickFilters, and
                  every other route reaches filters from the search sheet's
                  footer. */}
              <FiltersButton variant="compact" onApply={handleFilterApply} className="hidden sm:flex" />
            </div>
          )}
        </div>
        
        {/* Right section - Actions */}
        <div className="flex items-center justify-end gap-0.5 sm:gap-1 flex-shrink-0">
          {/* Page-nav group (design shell.jsx Header nav, 2026-07-16 rework):
              Explore · Searches · Liked · What's new as labeled pills right of
              the search input; labels collapse to icons below xl (design's
              1240px compact breakpoint). Explore/Searches hide below sm —
              production has no bottom nav to absorb them (D7). */}
          <Link
            href="/search"
            className={cn(navPillCls(pathname === '/search'), 'hidden xl:inline-flex')}
            title={t('header.explore', 'Explore')}
          >
            <Compass className="h-[18px] w-[18px]" />
            <span className={navPillLabelCls}>{t('header.explore', 'Explore')}</span>
          </Link>
          <SavedSearchesButton />
          <LikedButton />
          <NotificationBell />

          {/* Divider between the nav group and the seller/user cluster
              (design shell.jsx Header). */}
          <span className="mx-2 hidden h-6 w-px bg-outline sm:block" />

          {/* Roostr redesign nav (design shell.jsx Header; consolidated
              2026-07-15): ONE seller entry — the My listings pill. Posting
              starts from the dashboard, which carries its own "Post a
              listing" CTA. Active on /post too (it's the same seller flow).
              (The 2026-07-16 design revision moves My listings into an
              avatar menu + restores a Post pill — NOT adopted; the
              consolidation decision stands.) */}
          <Link
            href="/my-listings"
            className={`hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-[9px] text-[13.5px] font-semibold text-white shadow-e1 transition-transform active:scale-[0.97] ${
              pathname.startsWith('/my-listings') || pathname.startsWith('/post') ? 'bg-[#DC2626]' : 'bg-primary'
            }`}
            title={t('header.myListingsTip', 'My listings — post and manage from one place')}
          >
            <LayoutDashboard className="h-[17px] w-[17px]" />
            <span className="hidden min-[1700px]:inline">{t('header.myListings', 'My listings')}</span>
          </Link>

          {/* Command palette - icon + K - hidden on mobile.
              Stays OFF the search variant: that row also carries the 50vw
              input and has no width to spare. The avatar menu carries the
              palette entry instead (UserMenu), which is present on every
              route at every width. */}
          {variant === 'default' && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCommandPaletteOpen(true)}
              className="hidden xl:flex rounded-full xl:px-3 xl:h-10 xl:gap-1 xl:w-auto"
              title={`${t('header.commandSearch', 'Search')} (${isMac ? '⌘K' : 'Ctrl+K'})`}
            >
              {isMac ? (
                <>
                  <Command className="h-5 w-5" />
                  <span className="text-sm font-medium">K</span>
                </>
              ) : (
                <span className="text-sm font-medium">Ctrl K</span>
              )}
            </Button>
          )}
          
          {/* Planner link - temporarily disabled */}
          {/* <Button
            variant="ghost"
            size="icon"
            asChild
            className="hidden sm:flex rounded-full"
            title={t('header.planner', 'Planner')}
          >
            <Link href="/planner">
              <Kanban className="h-5 w-5" />
            </Link>
          </Button> */}
          
          {/* UI language toggle — visible at every width: this globe is the
              app's ONLY language switcher (UserMenu has none, guests have no
              menu at all), so hiding it below sm left phones stuck in one
              language. The flexible search pill absorbs the 36px. */}
          <div className="ml-1">
            <LanguageToggle />
          </div>

          {/* User menu */}
          <UserMenu />
        </div>
      </div>
    </header>
  )
}
